import { Navigate } from 'react-router-dom';
import { useModules } from '@/hooks/useModules';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

interface ModuleEnabledGateProps {
  id: string;
  children: React.ReactNode;
}

/**
 * Runtime guard for a module route subtree. Renders children only while the
 * module's flag is enabled (resolved from GET /api/admin/modules). A disabled
 * or errored module redirects to /admin/forbidden rather than rendering a dead
 * page — mirrors the backend EnsureModuleEnabled 404.
 *
 * See docs/architecture/MODULE_SYSTEM_SPEC.md §7.
 */
export function ModuleEnabledGate({ id, children }: ModuleEnabledGateProps) {
  const { data, isLoading, isError } = useModules();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const enabled = !isError && (data?.some((m) => m.id === id && m.enabled) ?? false);

  if (!enabled) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
}
